import React, { useState, useEffect, useCallback } from 'react';
import { forumHelpers, ForumCategory, ForumTopic, ForumReply, ForumTag, supabase } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Plus, MessageSquare, Users, TrendingUp, ArrowLeft, MoreHorizontal, ThumbsUp, ThumbsDown, Reply, Eye, MessageCircle, Settings, Heart, Tag, Image, Video, Link, Smile, Share2, Bookmark, Flag, Award, Send } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import CategoryManager from './CategoryManager';
import TopicEditor from './TopicEditor';
import ReplyEditor from './ReplyEditor';
import ActionButtons from './ActionButtons';
import ModernTopicView from './ModernTopicView';
import TopicCard from './TopicCard';

interface CommunityPageProps {
  isAdmin?: boolean;
  currentView?: string;
  categoryId?: string;
  topicId?: string;
  onViewChange?: (view: string) => void;
}

type CommunityView = 'categories' | 'category' | 'topic' | 'create';

const CommunityPage: React.FC<CommunityPageProps> = ({ 
  isAdmin = false,
  currentView,
  categoryId,
  topicId,
  onViewChange
}) => {
  const { user } = useAuth();
  const [view, setView] = useState<CommunityView>('categories');
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [topics, setTopics] = useState<ForumTopic[]>([]);
  const [replies, setReplies] = useState<ForumReply[]>([]);
  const [currentCategory, setCurrentCategory] = useState<ForumCategory | null>(null);
  const [currentTopic, setCurrentTopic] = useState<ForumTopic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [newTopicContent, setNewTopicContent] = useState('');
  const [newTopicCategory, setNewTopicCategory] = useState('');
  const [newReplyContent, setNewReplyContent] = useState('');
  
  // Category manager state
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  
  // Editor states
  const [showTopicEditor, setShowTopicEditor] = useState(false);
  const [showReplyEditor, setShowReplyEditor] = useState(false);
  const [editingTopic, setEditingTopic] = useState<ForumTopic | null>(null);
  const [editingReply, setEditingReply] = useState<ForumReply | null>(null);
  
  // Vote states
  const [topicVotes, setTopicVotes] = useState<Record<string, any>>({});
  const [replyVotes, setReplyVotes] = useState<Record<string, any>>({});
  const [voteLoading, setVoteLoading] = useState<string | null>(null);
  const [voteCounts, setVoteCounts] = useState<Record<string, number>>({});

  // Estados para estatísticas da comunidade
  const [communityStats, setCommunityStats] = useState<{
    total_users: number;
    total_topics: number;
    weekly_engagement: number;
  }>({
    total_users: 0,
    total_topics: 0,
    weekly_engagement: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Estados de loading específicos para navegação suave
  const [categoryLoading, setCategoryLoading] = useState<string | null>(null);
  const [topicLoading, setTopicLoading] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isLoadingTopic, setIsLoadingTopic] = useState(false);
  const [navigationProgress, setNavigationProgress] = useState(0);
  const [isDataReady, setIsDataReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isNavigatingToSpecificTopic, setIsNavigatingToSpecificTopic] = useState(false);
  const [lastNavigatedTopic, setLastNavigatedTopic] = useState<string | null>(null);

  // Notificar mudanças de view para o componente pai apenas quando mudar internamente
  const notifyViewChange = useCallback((newView: CommunityView) => {
    if (onViewChange && newView !== currentView) {
      onViewChange(newView);
    }
  }, [onViewChange, currentView]);

  // Atualizar view interno e notificar pai
  const updateView = useCallback((newView: CommunityView) => {
    setView(newView);
    notifyViewChange(newView);
  }, [notifyViewChange]);





  useEffect(() => {
    loadCategories();
    loadCommunityStats();

    // Configurar sincronização em tempo real
    const setupRealtimeSubscriptions = () => {
      // Sincronizar quando novos usuários se registram
      const usersSubscription = supabase
        .channel('users_changes')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'user_profiles'
        }, () => {
          loadCommunityStats();
        })
        .subscribe();

      // Sincronizar quando novos tópicos são criados
      const topicsSubscription = supabase
        .channel('topics_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'forum_topics'
        }, () => {
          if (currentCategory) {
            loadTopics(currentCategory.id);
          }
          loadCommunityStats();
        })
        .subscribe();

      // Sincronizar quando novas respostas são criadas
      const repliesSubscription = supabase
        .channel('replies_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'forum_replies'
        }, () => {
          if (currentTopic) {
            loadReplies(currentTopic.id);
          }
          loadCommunityStats();
        })
        .subscribe();

      return () => {
        usersSubscription.unsubscribe();
        topicsSubscription.unsubscribe();
        repliesSubscription.unsubscribe();
      };
    };

    const cleanup = setupRealtimeSubscriptions();
    return cleanup;
  }, [currentCategory, currentTopic]);

  // Efeito para inicializar isDataReady quando não há navegação específica
  useEffect(() => {
    if (!isInitializing && !isLoadingTopic && !loading) {
      // Se não estamos carregando nada específico, mostrar a interface
      if (!isDataReady) {
        setTimeout(() => {
          setIsDataReady(true);
        }, 200);
      }
    }
  }, [isInitializing, isLoadingTopic, loading, isDataReady]);

  // Efeito para limpar o estado de navegação após um tempo
  useEffect(() => {
    if (lastNavigatedTopic) {
      const timeoutId = setTimeout(() => {
        setLastNavigatedTopic(null);
      }, 5000); // Limpar após 5 segundos
      
      return () => clearTimeout(timeoutId);
    }
  }, [lastNavigatedTopic]);

  // Efeito para limpar estados quando o componente desmonta
  useEffect(() => {
    return () => {
      setIsNavigatingToSpecificTopic(false);
      setLastNavigatedTopic(null);
      setIsLoadingTopic(false);
      setNavigationProgress(0);
    };
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setIsInitializing(true);
      const { data, error } = await forumHelpers.getCategories();
      
      if (error) {
        setError('Erro ao carregar categorias');
        console.error('Erro ao carregar categorias:', error);
      } else {
        console.log('Dados brutos das categorias:', data);
        setCategories(data || []);
        console.log('CommunityPage: Categorias carregadas:', data);
      }
    } catch (err) {
      setError('Erro inesperado ao carregar categorias');
      console.error('Erro inesperado:', err);
    } finally {
      setLoading(false);
      setIsInitializing(false);
      // Aguardar um pouco para garantir que tudo está pronto
      setTimeout(() => {
        setIsDataReady(true);
      }, 500);
    }
  };



  const loadCommunityStats = async () => {
    try {
      setStatsLoading(true);
      
      // Buscar estatísticas da comunidade
      const { data: statsData, error: statsError } = await supabase
        .rpc('get_community_stats');

      if (statsError) {
        console.error('Erro ao carregar estatísticas:', statsError);
      } else {
        // A função RPC retorna um array, então pegamos o primeiro elemento
        const stats = Array.isArray(statsData) ? statsData[0] : statsData;
        setCommunityStats({
          total_users: stats?.total_users || 0,
          total_topics: stats?.total_topics || 0,
          weekly_engagement: stats?.weekly_engagement || 0
        });
        console.log('CommunityPage: Estatísticas carregadas:', stats);
      }
    } catch (err) {
      console.error('Erro inesperado ao carregar estatísticas:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadTopics = async (categoryId: string) => {
    try {
             const { data, error } = await forumHelpers.getTopics(categoryId);
      
      if (error) {
        setError('Erro ao carregar tópicos');
        console.error('Erro ao carregar tópicos:', error);
      } else {
        setTopics(data || []);
        
        // Carregar votos para cada tópico
        for (const topic of data || []) {
          await loadTopicVotes(topic.id);
        }
      }
    } catch (err) {
      setError('Erro inesperado ao carregar tópicos');
      console.error('Erro inesperado:', err);
    }
  };

  const loadReplies = async (topicId: string) => {
    try {
      const { data, error } = await forumHelpers.getReplies(topicId);
      
      if (error) {
        setError('Erro ao carregar respostas');
        console.error('Erro ao carregar respostas:', error);
      } else {
        setReplies(data || []);
        
        // Carregar votos para cada resposta
        for (const reply of data || []) {
          await loadReplyVotes(reply.id);
        }
      }
    } catch (err) {
      setError('Erro inesperado ao carregar respostas');
      console.error('Erro inesperado:', err);
    }
  };

  // Função para navegar para um tópico específico
  const navigateToSpecificTopic = useCallback(async (topicId: string, categoryId: string) => {
    // Prevenir múltiplas chamadas para o mesmo tópico
    if (isNavigatingToSpecificTopic) {
      console.log('Navegação já em andamento, ignorando chamada para:', topicId);
      return;
    }

    // Verificar se já estamos no tópico correto para evitar navegações desnecessárias
    if (view === 'topic' && currentTopic?.id === topicId) {
      console.log('Já estamos no tópico correto:', topicId);
      return;
    }

    // Verificar se já tentamos navegar para este tópico recentemente
    if (lastNavigatedTopic === topicId) {
      console.log('Tópico já foi navegado recentemente:', topicId);
      return;
    }
    
    console.log('Navegando para tópico:', topicId, 'categoria:', categoryId);
    
    // Marcar que estamos navegando
    setIsNavigatingToSpecificTopic(true);
    setLastNavigatedTopic(topicId);
    
    // Iniciar loading rigoroso - esconder toda a interface
    setIsLoadingTopic(true);
    setNavigationProgress(0);
    setIsDataReady(false);
    
    try {
      // Verificar se o usuário está autenticado
      if (!user) {
        console.error('Usuário não autenticado, redirecionando para categorias');
        setView('categories');
        setIsLoadingTopic(false);
        setIsDataReady(true);
        setIsNavigatingToSpecificTopic(false);
        return;
      }
      
      setNavigationProgress(20);
      
      // Primeiro, carregar as categorias se ainda não foram carregadas
      if (categories.length === 0) {
        await loadCategories();
      }
      
      setNavigationProgress(40);
      
      // Encontrar a categoria específica
      const category = categories.find(cat => cat.id === categoryId);
      if (!category) {
        console.error('Categoria não encontrada:', categoryId);
        // Em vez de voltar para categorias, tentar carregar o tópico diretamente
        console.log('Tentando carregar tópico diretamente...');
        const { data: topicData, error: topicError } = await forumHelpers.getTopic(topicId);
        
        if (topicError || !topicData) {
          console.error('Erro ao carregar tópico:', topicError);
          setView('categories');
          setIsLoadingTopic(false);
          setIsDataReady(true);
          setIsNavigatingToSpecificTopic(false);
          return;
        }
        
        setNavigationProgress(60);
        
        // Buscar a categoria do tópico
        const { data: categoryData, error: categoryError } = await supabase
          .from('forum_categories')
          .select('*')
          .eq('id', topicData.category_id)
          .single();
        
        if (categoryError || !categoryData) {
          console.error('Erro ao carregar categoria do tópico:', categoryError);
          setView('categories');
          setIsLoadingTopic(false);
          setIsDataReady(true);
          setIsNavigatingToSpecificTopic(false);
          return;
        }
        
        setNavigationProgress(80);
        
        // Usar a categoria encontrada
        setCurrentCategory(categoryData as unknown as ForumCategory);
        setCurrentTopic(topicData as ForumTopic);
        setView('topic');
        await loadReplies(topicId);
        
        setNavigationProgress(100);
        console.log('Navegação concluída para tópico:', topicData.title);
        
        // Aguardar um pouco antes de mostrar a interface
        setTimeout(() => {
          setIsLoadingTopic(false);
          setNavigationProgress(0);
          setIsDataReady(true);
          setIsNavigatingToSpecificTopic(false);
        }, 800);
        
        return;
      }
      
      setNavigationProgress(60);
      
      // Carregar os tópicos da categoria
      await loadTopics(categoryId);
      
      setNavigationProgress(80);
      
      // Encontrar o tópico específico
      const topic = topics.find(t => t.id === topicId);
      if (!topic) {
        console.error('Tópico não encontrado na lista:', topicId);
        // Tentar carregar o tópico diretamente
        const { data: topicData, error: topicError } = await forumHelpers.getTopic(topicId);
        
        if (topicError || !topicData) {
          console.error('Erro ao carregar tópico diretamente:', topicError);
          setView('category');
          setCurrentCategory(category);
          setIsLoadingTopic(false);
          setIsDataReady(true);
          setIsNavigatingToSpecificTopic(false);
          return;
        }
        
        // Adicionar o tópico à lista se não estiver
        setTopics(prev => {
          const exists = prev.find(t => t.id === topicId);
          return exists ? prev : [...prev, topicData];
        });
        
        setCurrentTopic(topicData as ForumTopic);
      } else {
        setCurrentTopic(topic);
      }
      
      // Navegar para o tópico
      setCurrentCategory(category);
      setView('topic');
      
      // Carregar as respostas do tópico
      await loadReplies(topicId);
      
      setNavigationProgress(100);
      console.log('Navegação concluída para tópico:', topic?.title || 'Tópico carregado diretamente');
      
      // Limpar o currentView para evitar loops
      if (onViewChange) {
        onViewChange('topic');
      }
      
      // Aguardar um pouco antes de mostrar a interface
      setTimeout(() => {
        setIsLoadingTopic(false);
        setNavigationProgress(0);
        setIsDataReady(true);
        setIsNavigatingToSpecificTopic(false);
      }, 800);
      
    } catch (error) {
      console.error('Erro ao navegar para tópico específico:', error);
      setView('categories');
      setIsLoadingTopic(false);
      setNavigationProgress(0);
      setIsDataReady(true);
      setIsNavigatingToSpecificTopic(false);
    }
  }, [user?.id]);

  // Sincronizar view interno com currentView externo apenas uma vez na inicialização
  useEffect(() => {
    // Prevenir execução se já estamos navegando
    if (isNavigatingToSpecificTopic) {
      return;
    }

    if (currentView && currentView !== view) {
      // Verificar se é uma subseção especial para navegar para um tópico específico
      if (currentView.startsWith('topic:')) {
        const [, topicId, categoryId] = currentView.split(':');
        if (topicId && categoryId) {
          // Navegar para o tópico específico apenas se não estivermos já no tópico correto
          if (view !== 'topic' || currentTopic?.id !== topicId) {
            // Usar setTimeout para debounce
            const timeoutId = setTimeout(() => {
              navigateToSpecificTopic(topicId, categoryId);
            }, 100);
            
            return () => clearTimeout(timeoutId);
          }
        }
      } else {
        setView(currentView as CommunityView);
      }
    }
  }, [currentView, view, currentTopic?.id, isNavigatingToSpecificTopic]);

  const loadCurrentTopicWithVotes = async () => {
    console.log('🔄 loadCurrentTopicWithVotes chamado para tópico:', currentTopic?.id);
    if (!currentTopic) {
      console.log('❌ currentTopic é null');
      return;
    }
    
    try {
      console.log('📡 Recarregando dados do tópico...');
      // Recarregar o tópico atual com votos atualizados
      const { data: topicData, error: topicError } = await forumHelpers.getTopic(currentTopic.id);
      
      if (!topicError && topicData) {
        console.log('✅ Tópico recarregado:', topicData);
        setCurrentTopic(topicData);
        await loadTopicVotes(topicData.id);
      } else {
        console.log('❌ Erro ao recarregar tópico:', topicError);
      }
      
      // Recarregar replies
      console.log('🔄 Recarregando replies...');
      await loadReplies(currentTopic.id);
    } catch (err) {
      console.error('❌ Erro ao recarregar tópico atual:', err);
    }
  };

  const handleCategoryClick = async (category: ForumCategory) => {
    // Prevenir múltiplos cliques
    if (categoryLoading || isNavigating) return;
    
    setCategoryLoading(category.id);
    setIsNavigating(true);
    setIsDataReady(false);
    
    try {
      setCurrentCategory(category);
      await loadTopics(category.id);
      updateView('category');
      
      // Salvar estado no localStorage
      localStorage.setItem('community_last_category', category.id);
      localStorage.removeItem('community_last_topic');
    } finally {
      setCategoryLoading(null);
      setIsNavigating(false);
      // Aguardar um pouco antes de mostrar a interface
      setTimeout(() => {
        setIsDataReady(true);
      }, 300);
    }
  };

  const handleTopicClick = async (topic: ForumTopic) => {
    // Prevenir múltiplos cliques
    if (topicLoading || isNavigating) return;
    
    setTopicLoading(topic.id);
    setIsNavigating(true);
    setIsDataReady(false);
    
    try {
      setCurrentTopic(topic);
      await loadReplies(topic.id);
      updateView('topic');
      
      // Salvar estado no localStorage
      if (currentCategory) {
        localStorage.setItem('community_last_category', currentCategory.id);
      }
      localStorage.setItem('community_last_topic', topic.id);
    } finally {
      setTopicLoading(null);
      setIsNavigating(false);
      // Aguardar um pouco antes de mostrar a interface
      setTimeout(() => {
        setIsDataReady(true);
      }, 300);
    }
  };

  const handleCreateTopic = () => {
    if (isNavigating) return;
    setIsDataReady(false);
    updateView('create');
    // Aguardar um pouco antes de mostrar a interface
    setTimeout(() => {
      setIsDataReady(true);
    }, 300);
  };

  const handleBackToCategories = () => {
    if (isNavigating) return;
    
    setIsDataReady(false);
    setCurrentCategory(null);
    setCurrentTopic(null);
    setTopics([]);
    setReplies([]);
    updateView('categories');
    
    // Limpar estado salvo
    localStorage.removeItem('community_last_category');
    localStorage.removeItem('community_last_topic');
    
    // Aguardar um pouco antes de mostrar a interface
    setTimeout(() => {
      setIsDataReady(true);
    }, 300);
  };

  const handleBackToCategory = () => {
    if (isNavigating) return;
    
    setIsDataReady(false);
    setCurrentTopic(null);
    setReplies([]);
    updateView('category');
    
    // Aguardar um pouco antes de mostrar a interface
    setTimeout(() => {
      setIsDataReady(true);
    }, 300);
  };

  const handleCategoriesUpdated = () => {
    loadCategories();
  };

  const loadTopicVotes = async (topicId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('get_topic_votes', { topic_id: topicId });

      if (!error && data) {
        setTopicVotes(prev => ({
          ...prev,
          [topicId]: data
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar votos do tópico:', err);
    }
  };

  const loadReplyVotes = async (replyId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('get_reply_votes', { reply_id: replyId });

      if (!error && data) {
        setReplyVotes(prev => ({
          ...prev,
          [replyId]: data
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar votos da resposta:', err);
    }
  };

  const handleVote = async (itemId: string, voteType: 'like', itemType: 'topic' | 'reply') => {
    console.log('🔴 handleVote chamado:', { itemId, voteType, itemType });
    if (!user) {
      console.log('❌ Usuário não autenticado em handleVote');
      return;
    }

    try {
      console.log('⏳ Iniciando processo de voto...');
      setVoteLoading(itemId);
      
      console.log('📡 Chamando RPC simple_vote...');
      const { data, error } = await supabase
        .rpc('simple_vote', {
          p_item_id: itemId,
          p_vote_type: voteType,
          p_item_type: itemType
        });

      if (error) {
        console.error('❌ Erro ao votar:', error);
      } else {
        console.log('✅ Voto registrado com sucesso:', data);
        
        // Verificar se data é um objeto com a propriedade action
        const voteData = data as { action?: string };
        
        // Atualizar apenas o contador específico usando um estado separado
        const newCount = voteData.action === 'added' ? 1 : -1;
        
        setVoteCounts(prev => ({
          ...prev,
          [itemId]: (prev[itemId] || 0) + newCount
        }));
        
        console.log(`🔄 Contador atualizado para ${itemId}: ${newCount > 0 ? 'incrementado' : 'decrementado'}`);
      }
    } catch (err) {
      console.error('❌ Erro inesperado ao votar:', err);
    } finally {
      setVoteLoading(null);
      console.log('🏁 Processo de voto finalizado');
    }
  };

  const handleEditTopic = (topic: ForumTopic) => {
    setEditingTopic(topic);
    setShowTopicEditor(true);
  };

  const handleEditReply = (reply: ForumReply) => {
    setEditingReply(reply);
    setShowReplyEditor(true);
  };

  const handleTopicUpdated = (updatedTopic: ForumTopic) => {
    setTopics(prev => prev.map(topic => 
      topic.id === updatedTopic.id ? updatedTopic : topic
    ));
    if (currentTopic?.id === updatedTopic.id) {
      setCurrentTopic(updatedTopic);
    }
  };

  const handleReplyUpdated = (updatedReply: ForumReply) => {
    setReplies(prev => prev.map(reply => 
      reply.id === updatedReply.id ? updatedReply : reply
    ));
  };

  const handleDeleteTopic = async (topicId: string) => {
    try {
      const { error } = await forumHelpers.deleteTopic(topicId);
      
      if (error) {
        setError('Erro ao deletar tópico');
        console.error('Erro ao deletar tópico:', error);
      } else {
        setTopics(prev => prev.filter(topic => topic.id !== topicId));
        if (currentTopic?.id === topicId) {
          handleBackToCategory();
        }
      }
    } catch (err) {
      setError('Erro inesperado ao deletar tópico');
      console.error('Erro inesperado:', err);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    try {
      const { error } = await forumHelpers.deleteReply(replyId);
      
      if (error) {
        setError('Erro ao deletar resposta');
        console.error('Erro ao deletar resposta:', error);
      } else {
        setReplies(prev => prev.filter(reply => reply.id !== replyId));
      }
    } catch (err) {
      setError('Erro inesperado ao deletar resposta');
      console.error('Erro inesperado:', err);
    }
  };

  const handleSubmitTopic = async () => {
    if (!user) return;

    try {
      const topicData = {
        title: newTopicTitle.trim(),
        content: newTopicContent.trim(),
        category_id: currentCategory?.id || newTopicCategory,
        created_by: user.id,
        is_pinned: false,
        is_locked: false,
        votes_count: 0
      };

      const { data, error } = await forumHelpers.createTopic(topicData);
      
      if (error) {
        setError('Erro ao criar discussão');
        console.error('Erro ao criar discussão:', error);
      } else {
        // Limpar formulário
        setNewTopicTitle('');
        setNewTopicContent('');
        setNewTopicCategory('');
        
        // Voltar para a categoria
        if (currentCategory) {
          await loadTopics(currentCategory.id);
          updateView('category');
        } else {
          handleBackToCategories();
        }
      }
    } catch (err) {
      setError('Erro inesperado ao criar discussão');
      console.error('Erro inesperado:', err);
    }
  };

  const handleSubmitReply = async (content: string) => {
    if (!user || !currentTopic) return;

    try {
      const replyData = {
        content: content.trim(),
        topic_id: currentTopic.id,
        created_by: user.id,
        parent_reply_id: undefined,
        is_solution: false,
        votes_count: 0
      };

      const { data, error } = await forumHelpers.createReply(replyData);
      
      if (error) {
        setError('Erro ao enviar resposta');
        console.error('Erro ao enviar resposta:', error);
      } else {
        // Limpar formulário
        setNewReplyContent('');
        
        // Recarregar respostas
        await loadReplies(currentTopic.id);
      }
    } catch (err) {
      setError('Erro inesperado ao enviar resposta');
      console.error('Erro inesperado:', err);
    }
  };

    const handleVoteTopic = async (topicId: string) => {
    console.log('🔴 handleVoteTopic chamado com topicId:', topicId);
    if (!user) {
      console.log('❌ Usuário não autenticado');
      return;
    }
    console.log('✅ Usuário autenticado, chamando handleVote...');
    await handleVote(topicId, 'like', 'topic');
  };

  // Função helper para obter o contador de likes atualizado
  const getVoteCount = (itemId: string, baseCount: number = 0) => {
    return (voteCounts[itemId] || 0) + baseCount;
  };

  const handleVoteReply = async (replyId: string) => {
    console.log('🔴 handleVoteReply chamado com replyId:', replyId);
    if (!user) {
      console.log('❌ Usuário não autenticado');
      return;
    }
    console.log('✅ Usuário autenticado, chamando handleVote...');
    await handleVote(replyId, 'like', 'reply');
  };

  if (loading || isInitializing) {
    return (
      <div className="flex-1 bg-[#121316] p-8 pt-24">
        <div className="container mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            {/* Loading Spinner Principal */}
            <div className="flex items-center justify-center mb-8">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-gray-700 border-t-[#0FFF55] rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-[#0FFF55]/30 rounded-full animate-ping"></div>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">Carregando Comunidade</h2>
            <p className="text-gray-400 text-center max-w-md">
              Preparando sua experiência na comunidade MasterClub...
            </p>
            
            {/* Shimmer effect para simular carregamento */}
            <div className="mt-8 w-full max-w-4xl">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="h-64 bg-gray-700 rounded-xl loading-shimmer"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading suave para navegação entre tópicos
  if (isLoadingTopic) {
    return (
      <div className="flex-1 bg-[#121316] p-8 pt-24">
        <div className="container mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            {/* Progress Bar */}
            <div className="w-full max-w-md mb-8">
              <div className="flex justify-between text-sm text-gray-400 mb-2">
                <span>Carregando discussão...</span>
                <span>{navigationProgress}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-[#0FFF55] h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${navigationProgress}%` }}
                ></div>
              </div>
            </div>
            
            {/* Loading Spinner */}
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-gray-700 border-t-[#0FFF55] rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-[#0FFF55]/30 rounded-full animate-ping"></div>
              </div>
            </div>
            
            <p className="text-gray-400 text-center">
              Preparando sua discussão...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Não renderizar nada até que os dados estejam prontos
  if (!isDataReady) {
    return (
      <div className="flex-1 bg-[#121316] p-8 pt-24">
        <div className="container mx-auto">
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-12 h-12 border-4 border-gray-700 border-t-[#0FFF55] rounded-full animate-spin mb-4"></div>
            <p className="text-gray-400 text-center">Finalizando carregamento...</p>
          </div>
        </div>
      </div>
    );
  }


  // Render Categories View
  if (view === 'categories') {
    return (
      <>
        <div className={`flex-1 bg-[#121316] p-8 pt-24 transition-all duration-500 ease-in-out animate-fade-in ${
          isNavigating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
        }`}>
          <div className="container mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    Comunidade MasterClub
                  </h1>
                  <p className="text-gray-400 text-sm sm:text-base">
                    Conecte-se com outros empreendedores, compartilhe experiências e aprenda juntos
                  </p>
                </div>
                {user && (
                  <Button 
                    onClick={handleCreateTopic}
                    className="flex items-center gap-2 bg-[#0FFF55] text-[#121316] hover:bg-[#0dd149] transition-all duration-200 hover:scale-105 self-start sm:self-center"
                  >
                    <Plus className="w-4 h-4" />
                    Nova Discussão
                  </Button>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card className="bg-[#1a1d23] border-[#1f2329]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-8 h-8 text-blue-400" />
                    <div>
                      <p className="text-sm text-gray-400">Discussões</p>
                      <div className="text-2xl font-bold text-white">
                        {statsLoading ? (
                          <div className="animate-pulse bg-gray-600 h-8 w-16 rounded"></div>
                        ) : (
                          communityStats.total_topics.toLocaleString('pt-BR')
                        )}
                      </div>
                      <p className="text-xs text-gray-500">Total de tópicos criados</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#1a1d23] border-[#1f2329]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-green-400" />
                    <div>
                      <p className="text-sm text-gray-400">Membros</p>
                      <div className="text-2xl font-bold text-white">
                        {statsLoading ? (
                          <div className="animate-pulse bg-gray-600 h-8 w-16 rounded"></div>
                        ) : (
                          communityStats.total_users.toLocaleString('pt-BR')
                        )}
                      </div>
                      <p className="text-xs text-gray-500">Usuários ativos na plataforma</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#1a1d23] border-[#1f2329]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                    <div>
                      <p className="text-sm text-gray-400">Engajamento</p>
                      <div className="text-2xl font-bold text-white">
                        {statsLoading ? (
                          <div className="animate-pulse bg-gray-600 h-8 w-16 rounded"></div>
                        ) : (
                          communityStats.weekly_engagement.toLocaleString('pt-BR')
                        )}
                      </div>
                      <p className="text-xs text-gray-500">Likes e respostas na última semana</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

                        {/* Categories Grid */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-white mb-6">
                Áreas de Discussão
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {categories.map((category, index) => (
                  <Card 
                    key={category.id}
                    className={`cursor-pointer hover:shadow-lg transition-all duration-500 ease-out hover:scale-105 bg-[#1a1d23] border-[#1f2329] hover:border-[#0FFF55]/40 hover:shadow-[0_0_20px_rgba(15,255,85,0.3)] group overflow-hidden ${
                      categoryLoading === category.id ? 'opacity-75 pointer-events-none' : ''
                    }`}
                    style={{
                      animationDelay: `${index * 100}ms`,
                      animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                    onClick={() => handleCategoryClick(category)}
                  >
                    {/* Banner Superior */}
                    <div className="relative h-32 bg-gradient-to-r from-gray-800 to-gray-700 overflow-hidden">
                      <div 
                        className="absolute inset-0 bg-cover bg-center opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ 
                          backgroundImage: (category.banner_url || category.banner_image) ? `url('${category.banner_url || category.banner_image}')` : undefined,
                          backgroundColor: category.color
                        }}
                      />
                      {/* Fallback pattern quando imagem não existe */}
                      <div 
                        className="absolute inset-0 opacity-60 group-hover:opacity-80 transition-opacity duration-300"
                        style={{
                          background: `linear-gradient(135deg, ${category.color}40 0%, ${category.color}20 50%, ${category.color}60 100%)`,
                          backgroundImage: `radial-gradient(circle at 30% 20%, ${category.color}30 0%, transparent 50%), radial-gradient(circle at 70% 80%, ${category.color}20 0%, transparent 50%)`
                        }}
                      />
                      {/* Overlay com gradiente */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      
                      {/* Ícone da categoria no banner */}
                      <div className="absolute top-3 right-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-xl bg-white/20 backdrop-blur-sm border border-white/30 group-hover:bg-white/30 transition-all duration-300"
                          style={{ color: category.color }}
                        >
                          {categoryLoading === category.id ? (
                            <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            category.icon
                          )}
                        </div>
                      </div>
                      
                      {/* Efeito de iluminação verde no hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-[#0FFF55]/0 via-[#0FFF55]/0 to-[#0FFF55]/0 group-hover:from-[#0FFF55]/10 group-hover:via-[#0FFF55]/5 group-hover:to-[#0FFF55]/10 transition-all duration-500" />
                    </div>
                    
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <CardTitle className="text-lg font-semibold text-white group-hover:text-[#0FFF55] transition-colors duration-300">
                            {category.name}
                          </CardTitle>
                          {category.description && (
                            <p className="text-sm text-gray-400 mt-1 line-clamp-2 group-hover:text-gray-300 transition-colors duration-300">
                              {category.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        {category.custom_tag && category.custom_tag.trim() !== '' ? (
                          // Exibir etiqueta personalizada se existir e não estiver vazia
                          <Badge 
                            variant="outline"
                            style={{ 
                              color: '#0FFF55',
                              borderColor: '#0FFF55',
                              backgroundColor: 'transparent'
                            }}
                            className="group-hover:scale-105 transition-transform duration-300 rounded-full"
                          >
                            {category.custom_tag}
                          </Badge>
                        ) : (
                          // Exibir etiqueta padrão "Ativa"
                          <Badge 
                            variant="secondary"
                            style={{ backgroundColor: category.color, color: 'white' }}
                            className="group-hover:scale-105 transition-transform duration-300"
                          >
                            Ativa
                          </Badge>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-gray-400 hover:text-[#0FFF55] group-hover:text-[#0FFF55] transition-colors duration-300"
                        >
                          Ver discussões →
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Admin Section */}
            {isAdmin && (
              <div className="mt-12 p-6 bg-[#1a1d23] rounded-lg border border-[#1f2329]">
                <h3 className="text-xl font-semibold text-white mb-4">
                  Área Administrativa
                </h3>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    variant="outline"
                    onClick={() => setShowCategoryManager(true)}
                    className="flex items-center gap-2 whitespace-nowrap"
                  >
                    <Settings className="w-4 h-4" />
                    Gerenciar Categorias
                  </Button>

                  <Button variant="outline" className="whitespace-nowrap">
                    Estatísticas do Fórum
                  </Button>
                  <Button variant="outline" className="whitespace-nowrap">
                    Moderar Conteúdo
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Category Manager Modal */}
        {showCategoryManager && (
          <CategoryManager
            onClose={() => setShowCategoryManager(false)}
            onCategoriesUpdated={handleCategoriesUpdated}
          />
        )}
      </>
    );
  }

  // Render Category View
  if (view === 'category' && currentCategory) {
    return (
      <>
        <div className={`flex-1 bg-[#121316] p-8 pt-24 transition-all duration-500 ease-in-out animate-fade-in ${
          isNavigating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
        }`}>
          <div className="container mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <Button 
                    variant="ghost" 
                    onClick={handleBackToCategories}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-200 hover:scale-105 self-start"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar
                  </Button>
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all duration-200 hover:scale-110 shrink-0"
                      style={{ backgroundColor: currentCategory.color + '20' }}
                    >
                      {currentCategory.icon}
                    </div>
                    <div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-white">
                        {currentCategory.name}
                      </h1>
                      <p className="text-gray-400 text-sm sm:text-base">
                        {currentCategory.description}
                      </p>
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={handleCreateTopic}
                  className="bg-[#0FFF55] text-[#121316] hover:bg-[#0dd149] transition-all duration-200 hover:scale-105 self-start lg:self-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Discussão
                </Button>
              </div>
            </div>

            {/* Topics List */}
            <div className="space-y-4">
              {topics.map((topic) => (
                            <TopicCard
              key={topic.id}
              topic={topic}
              onClick={handleTopicClick}
              getVoteCount={getVoteCount}
              isLoading={topicLoading === topic.id}
              showSaveButton={true}
            />
              ))}
            </div>

            {topics.length === 0 && (
              <div className="text-center py-12">
                <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-400 mb-2">
                  Nenhuma discussão ainda
                </h3>
                <p className="text-gray-500 mb-6">
                  Seja o primeiro a iniciar uma discussão nesta categoria!
                </p>
                <Button 
                  onClick={handleCreateTopic}
                  className="bg-[#0FFF55] text-[#121316] hover:bg-[#0dd149]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeira Discussão
                </Button>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // Render Topic View
  if (view === 'topic' && currentTopic) {
    return (
      <>
        <div className={`transition-all duration-500 ease-in-out ${
          isNavigating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
        }`}>
          <ModernTopicView
          topic={currentTopic}
          replies={replies}
          user={user}
          categoryName={currentCategory?.name}
          recentDiscussions={topics.filter(t => t.id !== currentTopic?.id).slice(0, 5)}
          onBack={handleBackToCategory}
          onSubmitReply={handleSubmitReply}
          newReplyContent={newReplyContent}
          setNewReplyContent={setNewReplyContent}
          onMediaUpload={(files) => {
            // Implementar upload de mídia
            console.log('Upload de mídia:', files);
          }}
          onReplyToReply={(replyId, authorName) => {
            // Implementar resposta a resposta específica
            setNewReplyContent(`@${authorName} `);
            console.log('Responder a:', replyId, authorName);
          }}
          onDiscussionClick={(discussion) => {
            handleTopicClick(discussion);
          }}
          onViewAllDiscussions={() => {
            handleBackToCategory();
          }}
          onVoteTopic={handleVoteTopic}
          onVoteReply={handleVoteReply}
          voteLoading={voteLoading}
          getVoteCount={getVoteCount}
        />
        </div>

        {/* Editors */}
        {showTopicEditor && (
          <TopicEditor
            topic={editingTopic}
            onClose={() => {
              setShowTopicEditor(false);
              setEditingTopic(null);
            }}
            onTopicUpdated={handleTopicUpdated}
          />
        )}
        {showReplyEditor && (
          <ReplyEditor
            reply={editingReply}
            onClose={() => {
              setShowReplyEditor(false);
              setEditingReply(null);
            }}
            onReplyUpdated={handleReplyUpdated}
          />
        )}
      </>
    );
  }

  // Render Create Topic View
  if (view === 'create') {
    return (
      <>
        <div className={`flex-1 bg-[#121316] p-8 pt-24 transition-all duration-500 ease-in-out animate-fade-in ${
          isNavigating ? 'opacity-50 scale-95' : 'opacity-100 scale-100'
        }`}>
          <div className="container mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <Button 
                  variant="ghost" 
                  onClick={handleBackToCategories}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-all duration-200 hover:scale-105 self-start"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Voltar
                </Button>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    Nova Discussão
                  </h1>
                  <p className="text-gray-400 text-sm sm:text-base">
                    Compartilhe sua dúvida ou experiência com a comunidade
                  </p>
                </div>
              </div>
            </div>

            {/* Create Topic Form */}
            <Card className="bg-[#1a1d23] border-[#1f2329]">
              <CardHeader>
                <CardTitle className="text-white">
                  {currentCategory 
                    ? `Nova Discussão em ${currentCategory.name}` 
                    : 'Criar Nova Discussão'
                  }
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="topic-title" className="text-white">Título</Label>
                    <Input
                      id="topic-title"
                      placeholder="Digite o título da discussão..."
                      value={newTopicTitle}
                      onChange={(e) => setNewTopicTitle(e.target.value)}
                      className="bg-[#0f1114] border-[#1f2329] text-white placeholder-gray-500"
                    />
                  </div>
                  
                  {/* Mostrar campo de categoria apenas se não estiver dentro de uma categoria específica */}
                  {!currentCategory && (
                    <div>
                      <Label htmlFor="topic-category" className="text-white">Categoria</Label>
                      <Select value={newTopicCategory} onValueChange={setNewTopicCategory}>
                        <SelectTrigger className="bg-[#0f1114] border-[#1f2329] text-white">
                          <SelectValue placeholder="Selecione uma categoria" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1a1d23] border-[#1f2329]">
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id} className="text-white hover:bg-[#0FFF55]/10">
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Mostrar categoria atual se estiver dentro de uma categoria específica */}
                  {currentCategory && (
                    <div>
                      <Label className="text-white">Categoria</Label>
                      <div className="flex items-center gap-2 p-3 bg-[#0f1114] border border-[#1f2329] rounded-md">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: currentCategory.color }}
                        />
                        <span className="text-white font-medium">{currentCategory.name}</span>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="topic-content" className="text-white">Conteúdo</Label>
                    <Textarea
                      id="topic-content"
                      placeholder="Escreva o conteúdo da sua discussão..."
                      value={newTopicContent}
                      onChange={(e) => setNewTopicContent(e.target.value)}
                      rows={6}
                      className="bg-[#0f1114] border-[#1f2329] text-white placeholder-gray-500"
                    />
                  </div>
                  
                  <Button onClick={handleSubmitTopic}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Discussão
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default CommunityPage; 